'use client'

import * as React from 'react'
import confetti from 'canvas-confetti'
import { AlertCircleIcon, ArrowRight } from 'lucide-react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  LottieAnimation,
  NumberFlow,
} from '@ui/web/components'
import { PuzzleIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

export interface SuccessData {
  totalXp: number | null
  lessonTitle?: string
  completedAt?: Date | string
  onBack?: React.MouseEventHandler<HTMLButtonElement>
  backDisabled?: boolean
  onNext?: React.MouseEventHandler<HTMLButtonElement>
  nextDisabled?: boolean
  extraCTA?: React.ReactNode
}

type ModalState =
  | { type: 'loading' }
  | { type: 'error'; message?: string; onRetry?: () => void }
  | ({ type: 'success' } & SuccessData)

interface XpEarnedModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  state: ModalState
  withConfetti?: boolean
  disabledClose?: boolean
}

const xpShieldUrl = getStaticAssetUrl('illustration/check-purple.png')

function LoadingContent() {
  return (
    <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:bg-neutrals-white srd:p-4 srd:rounded-xl'>
      <LottieAnimation
        animationConfig={{
          path: getStaticAssetUrl('lottie/loading-paperplane.json'),
          loop: true,
          autoplay: true,
        }}
        style={{ width: '100%', maxWidth: 'calc(var(--spacing) * 70)', height: 'fit-content' }}
      />
    </div>
  )
}

function ErrorContent({ message, onClose, onRetry }: { message?: string; onClose: () => void; onRetry?: () => void }) {
  return (
    <div className='srd:w-full srd:bg-neutrals-white srd:p-4 srd:rounded-xl'>
      <div className='srd:w-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:text-center'>
        <AlertCircleIcon className='srd:w-12 srd:h-12 srd:text-main-red-100' />
        <p className={cn(typographyVariants({ variant: 'headline-sm' }))}>Terjadi Kesalahan</p>
        <p className={cn(typographyVariants({ variant: 'body-md' }))}>
          {message ?? 'Gagal memuat data XP. Silakan coba lagi.'}
        </p>
        <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:gap-4 srd:flex-wrap srd:mt-6'>
          <Button variant='outline' onClick={onClose}>
            Tutup
          </Button>
          {onRetry && (
            <Button variant='primary' onClick={onRetry}>
              Coba Lagi
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export const XpEarnedContent = React.memo(function XpEarnedContent({ data }: { data: SuccessData }) {
  return (
    <div className='srd:w-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-2 srd:text-center srd:text-neutral-black'>
      <img src={xpShieldUrl} alt='' width={110} height={110} draggable={false} />

      {data.lessonTitle && (
        <p className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-grey-100')}>{data.lessonTitle}</p>
      )}

      <p className={cn(typographyVariants({ variant: 'headline-sm' }))}>Berhasil dapat poin</p>

      {data.totalXp !== null ? (
        <NumberFlow value={data.totalXp} className={cn(typographyVariants({ variant: 'display' }))} />
      ) : (
        <span className={cn(typographyVariants({ variant: 'display' }))}>???</span>
      )}

      {data.completedAt && (
        <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>
          {formatDate(new Date(data.completedAt), { dateStyle: 'long', timeStyle: 'short' })}
        </p>
      )}

      {(data.onBack !== undefined || data.onNext !== undefined) && (
        <div className='srd:w-full srd:flex srd:items-center srd:justify-center srd:gap-4 srd:flex-wrap srd:mt-6'>
          <Button variant='outline' disabled={data.backDisabled} onClick={data.onBack}>
            <PuzzleIcon className='srd:w-4 srd:h-4 srd:mr-2' />
            Kembali
          </Button>

          {data.extraCTA}

          <Button variant='gradient' disabled={data.nextDisabled} onClick={data.onNext}>
            Berikutnya
            <ArrowRight className='srd:w-4 srd:h-4 srd:ml-2 srd:text-neutrals-white' />
          </Button>
        </div>
      )}
    </div>
  )
})

const MODAL_CLASS = cn('srd:max-w-lg srd:max-h-[95dvh] srd:overflow-y-auto srd:z-[9999]')

export const XpEarnedModal = React.memo(function XpEarnedModal({
  open,
  onOpenChange,
  state,
  withConfetti = false,
  disabledClose = true,
}: XpEarnedModalProps) {
  const fireConfetti = React.useCallback(() => {
    const count = 200
    const defaults = {
      origin: { y: 0.7 },
      spread: 360,
      ticks: 100,
      gravity: 0,
      decay: 0.94,
      startVelocity: 30,
      zIndex: 10000,
    }
    function fire(particleRatio: number, opts: confetti.Options) {
      void confetti({ ...defaults, ...opts, particleCount: Math.floor(count * particleRatio) })
    }
    fire(0.25, { spread: 26, startVelocity: 55 })
    fire(0.2, { spread: 60 })
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 })
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 })
    fire(0.1, { spread: 120, startVelocity: 45 })
  }, [])

  React.useEffect(() => {
    if (open && withConfetti && state.type === 'success') fireConfetti()
  }, [open, withConfetti, state.type, fireConfetti])

  const isCloseable = state.type === 'error' || !disabledClose
  const showClose = state.type !== 'loading' && isCloseable

  const content =
    state.type === 'loading' ? (
      <LoadingContent />
    ) : state.type === 'error' ? (
      <ErrorContent message={state.message} onClose={() => onOpenChange(false)} onRetry={state.onRetry} />
    ) : (
      <XpEarnedContent data={state} />
    )

  const srTitle =
    state.type === 'loading' ? 'Memuat XP' : state.type === 'error' ? 'Terjadi kesalahan' : 'XP yang didapatkan'

  return (
    <Dialog
      open={open}
      onOpenChange={
        isCloseable
          ? onOpenChange
          : () => {
              /* empty handler */
            }
      }
    >
      <DialogContent showCloseButton={showClose} className={MODAL_CLASS}>
        <DialogHeader className='srd:sr-only'>
          <DialogTitle>{srTitle}</DialogTitle>
          <DialogDescription>{srTitle}</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  )
})
